import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { WorkOrdersService } from '../../services/work-orders.service';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-work-orders',
  templateUrl: './work-orders.component.html',
  styleUrls: ['./work-orders.component.scss']
})
export class WorkOrdersComponent implements OnInit, OnDestroy {
  workOrders: any[] = [];
  filteredOrders: any[] = [];

  // Debounce
  private filterSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Filtros
  searchTerm: string = '';
  filterId: number | null = null;
  selectedStatus: any = null; // 🔄 Cambiado para p-dropdown
  selectedCancellationFilter: string = 'active';
  
  // 🆕 Filtros de fecha (solo fecha de creación para work orders)
  startDate: Date | null = null;
  endDate: Date | null = null;
  
  // 🆕 Ordenamiento
  sortOrder: 'asc' | 'desc' = 'desc'; // Por defecto descendente (más recientes primero)

  // 🆕 Paginación
  pageNumber: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;
  paginatedOrders: any[] = [];

  // Métricas
  totalAmount: number = 0;

  // Modal anulación
  showConfirmationModal = false;
  cancellationReason = '';
  selectedOrderForCancellation: any = null;

  constructor(
    private workOrdersService: WorkOrdersService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.filterSubject.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.loadingService.show();
    
    // 🆕 USAR SERVICIO PAGINADO DEL LADO SERVIDOR
    const parameters: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm,
      sortBy: 'Id',
      sortOrder: this.sortOrder,
      startDate: this.startDate,
      endDate: this.endDate,
      dateType: 'Creation', // Work orders solo tienen fecha de creación
      cancellationFilter: this.selectedCancellationFilter,
      status: this.selectedStatus
    };

    if (this.filterId) parameters.filterId = this.filterId;

    this.workOrdersService.getWorkOrdersPaged(parameters).subscribe({
      next: (response) => {
        this.workOrders = response.data || [];
        this.totalRecords = response.totalRecords || 0;
        this.paginatedOrders = this.workOrders; // Ya viene paginado del servidor
        
        this.calculateMetrics();
        this.loadingService.hide();
      },
      error: (err) => {
        console.error(err);
        this.toastService.showToast('error', 'Error al cargar las órdenes de trabajo');
        this.loadingService.hide();
      }
    });
  }

  private calculateMetrics(): void {
    this.totalAmount = this.workOrders.reduce((sum, order) => sum + order.totalAmount, 0);
  }

  applyFilters(): void {
    this.filteredOrders = this.workOrders.filter(order => {
      const matchesSearch = Object.values(order).some(value =>
        String(value).toLowerCase().includes(this.searchTerm.toLowerCase())
      );

      const matchesStatus = this.selectedStatus ?
        order.status === this.selectedStatus : true;

      const matchesDate = this.checkDateRange(order.createdAt);

      // 🆕 FILTRO POR ESTADO DE ANULACIÓN (no necesario si ya se cargaron filtrados desde el backend)
      // Pero se mantiene para consistencia en caso de que los datos ya estén en memoria
      const matchesCancellation = this.selectedCancellationFilter === 'all' ? true :
        this.selectedCancellationFilter === 'cancelled' ? order.isCancelled === true :
          order.isCancelled !== true; // 'active'

      return matchesSearch && matchesStatus && matchesDate && matchesCancellation;
    });

    // 🆕 Aplicar ordenamiento por ID
    this.filteredOrders.sort((a, b) => {
      return this.sortOrder === 'desc' ? b.id - a.id : a.id - b.id;
    });

    // 🆕 Aplicar paginación después de filtros
    this.totalRecords = this.filteredOrders.length;
    this.applyPagination();
  }
  
  // 🆕 Método para cambiar ordenamiento
  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    this.loadData();
  }
  
  // 🆕 Método para obtener documento del tercero
  getThirdPartyDocument(order: any): string {
    if (order.thirdParty) {
      return `${order.thirdParty.typeDocument || ''} ${order.thirdParty.documentId || ''}`.trim();
    }
    return 'N/A';
  }

  // 🆕 Método para aplicar paginación
  applyPagination(): void {
    const startIndex = (this.pageNumber - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedOrders = this.filteredOrders.slice(startIndex, endIndex);
  }

  // 🆕 Método para cambio de página
  onPageChange(event: any): void {
    this.pageNumber = event.page + 1;
    this.pageSize = event.rows;
    this.loadData();
  }

  // Método para cambio de filtros (con debounce)
  onFilterChange(): void {
    this.filterSubject.next();
  }

  // 🆕 Método para verificar rango de fechas
  private checkDateRange(orderDate: string): boolean {
    if (!this.startDate && !this.endDate) return true;

    if (!orderDate) return true;

    const date = new Date(orderDate);
    date.setHours(0, 0, 0, 0);

    if (this.startDate) {
      const start = new Date(this.startDate);
      start.setHours(0, 0, 0, 0);
      if (date < start) return false;
    }

    if (this.endDate) {
      const end = new Date(this.endDate);
      end.setHours(23, 59, 59, 999);
      if (date > end) return false;
    }

    return true;
  }

  getProgress(order: any): number {
    const daysPassed = Math.floor((Date.now() - new Date(order.createdAt).getTime()) / (1000 * 3600 * 24));
    return Math.min((daysPassed / 7) * 100, 100);
  }

  onCancellationFilterChange(): void {
    this.pageNumber = 1;
    this.loadData();
  }

  showCancelModal(order: any): void {
    if (order.isCancelled) {
      this.toastService.showToast('warning', 'Esta orden ya está anulada');
      return;
    }
    this.selectedOrderForCancellation = order;
    this.cancellationReason = '';
    this.showConfirmationModal = true;
  }

  async onConfirmCancellation(): Promise<void> {
    if (!this.cancellationReason.trim()) {
      this.toastService.showToast('error', 'Debe especificar el motivo de anulación');
      return;
    }
    if (!this.selectedOrderForCancellation) return;

    try {
      this.loadingService.show();
      await this.workOrdersService
        .cancelWorkOrder(this.selectedOrderForCancellation.id, this.cancellationReason.trim())
        .toPromise();
      this.toastService.showToast('success', '✅ Orden de trabajo anulada exitosamente');
      this.closeConfirmationModal();
      this.loadData();
    } catch (error: any) {
      console.error('Error al anular orden de trabajo:', error);
      const errorMessage = error?.error?.errorMessage || error?.error || 'Error al anular la orden de trabajo';
      this.toastService.showToast('error', errorMessage);
    } finally {
      this.loadingService.hide();
    }
  }

  onCancelCancellation(): void {
    this.closeConfirmationModal();
  }

  private closeConfirmationModal(): void {
    this.showConfirmationModal = false;
    this.selectedOrderForCancellation = null;
    this.cancellationReason = '';
  }

  duplicateWorkOrder(orderId: number): void {
    this.router.navigate(['/work-orders/new'], { queryParams: { duplicateId: orderId } });
  }

  // 🆕 MÉTODOS DE DESCARGA DE PDF
  isDownloadingPdf: { [key: number]: boolean } = {};

  downloadPdf(order: any): void {
    if (this.isDownloadingPdf[order.id]) return;

    this.isDownloadingPdf[order.id] = true;
    this.workOrdersService.downloadPdf(order.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `OrdenTrabajo_${order.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPdf[order.id] = false;
        this.toastService.showToast('success', 'PDF descargado correctamente');
      },
      error: (error) => {
        console.error('Error downloading PDF:', error);
        this.isDownloadingPdf[order.id] = false;
        this.toastService.showToast('error', 'Error al descargar el PDF');
      }
    });
  }
}
